/**
 * Weekly Trends — week-by-week comparison of Position Folders opened vs offers
 * sent, so the team can see whether we're opening more/fewer orders and sending
 * more/fewer offers over time.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ResponsiveContainer, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, XAxis, YAxis, ComposedChart, Line,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, FolderPlus, Send, RefreshCw, Loader2,
  Download, Mail, X,
} from "lucide-react";
import { Navbar } from "../components/Navbar";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: 8, label: "8 weeks" },
  { value: 12, label: "12 weeks" },
  { value: 26, label: "26 weeks" },
  { value: 52, label: "52 weeks" },
];

function TrendPill({ direction, weeks }) {
  const up = direction === "up";
  const down = direction === "down";
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "text-emerald-700 bg-emerald-100" : down ? "text-red-700 bg-red-100" : "text-slate-600 bg-slate-100";
  const label = up ? `Up over ${weeks} wks` : down ? `Down over ${weeks} wks` : `Flat over ${weeks} wks`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${color}`} title={`Overall direction across the last ${weeks} weeks (first half vs second half average). This is a period trend, not the week-over-week change shown below.`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
}

function SummaryCard({ icon: Icon, title, accent, trend, unit, weeks }) {
  const wow = trend?.wow_change ?? 0;
  const wowUp = wow > 0;
  const wowDown = wow < 0;
  const accentCls = accent === "emerald"
    ? { box: "bg-emerald-100", icon: "text-emerald-600" }
    : { box: "bg-indigo-100", icon: "text-indigo-600" };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accentCls.box}`}>
            <Icon className={`h-5 w-5 ${accentCls.icon}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{trend?.total ?? 0}<span className="ml-1 text-sm font-medium text-slate-400">{unit}</span></p>
          </div>
        </div>
        <TrendPill direction={trend?.direction} weeks={weeks} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-slate-50 py-2">
          <p className="text-xs text-slate-400">Avg / week</p>
          <p className="text-sm font-semibold text-slate-800">{trend?.avg_per_week ?? 0}</p>
        </div>
        <div className="rounded-lg bg-slate-50 py-2">
          <p className="text-xs text-slate-400">Last full week</p>
          <p className="text-sm font-semibold text-slate-800">{trend?.last_full_week ?? 0}</p>
        </div>
        <div className="rounded-lg bg-slate-50 py-2">
          <p className="text-xs text-slate-400">vs prev week</p>
          <p className={`text-sm font-semibold ${wowUp ? "text-emerald-600" : wowDown ? "text-red-600" : "text-slate-800"}`}>
            {wowUp ? "+" : ""}{wow}{trend?.wow_pct != null ? ` (${wowUp ? "+" : ""}${trend.wow_pct}%)` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function WeeklyTrendsPage() {
  const token = localStorage.getItem("token");
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API_URL}/analytics/weekly-trends?weeks=${weeks}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(true);
      toast.error("Failed to load weekly trends");
    } finally {
      setLoading(false);
    }
  }, [token, weeks]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const series = data?.series || [];
  const rows = [...series].reverse(); // newest first for the table

  const exportCsv = () => {
    if (!series.length) return;
    const head = ["Week start", "Week end", "ISO week", "PFs opened", "Offers sent", "Partial week"];
    const lines = [head.join(",")];
    series.forEach((s) => {
      lines.push([s.week_start, s.week_end, s.iso_week, s.pfs_opened, s.offers_sent, s.partial ? "yes" : "no"].join(","));
    });
    const pf = data?.pf_trend || {}, off = data?.offer_trend || {};
    lines.push("");
    lines.push(["Summary", "", "", "PFs opened", "Offers sent"].join(","));
    lines.push(["Total", "", "", pf.total ?? 0, off.total ?? 0].join(","));
    lines.push(["Average / week", "", "", pf.avg_per_week ?? 0, off.avg_per_week ?? 0].join(","));
    lines.push(["Last full week", "", "", pf.last_full_week ?? 0, off.last_full_week ?? 0].join(","));
    lines.push(["Direction over period", "", "", pf.direction ?? "", off.direction ?? ""].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `weekly-trends-${weeks}wk-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    toast.success("CSV exported");
  };

  const sendEmail = async () => {
    const list = recipients.split(/[,;\s]+/).map((r) => r.trim()).filter((r) => r.includes("@"));
    if (!list.length) { toast.error("Enter at least one valid email address"); return; }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/analytics/weekly-trends/email`,
        { weeks, recipients: list, message: emailMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent to ${res.data.recipients.join(", ")}`);
      setEmailOpen(false);
      setRecipients("");
      setEmailMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Weekly Trends</h1>
            <p className="mt-1 text-sm text-slate-500">Position Folders opened vs offers sent, week by week</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 bg-white p-1">
              {RANGE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setWeeks(o.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${weeks === o.value ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`weeks-${o.value}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              data-testid="export-csv"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              data-testid="open-email-modal"
            >
              <Mail className="h-4 w-4" /> Email report
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              data-testid="refresh-trends"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : error && !data ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 text-center" data-testid="trends-error">
            <p className="text-lg font-semibold text-red-700">Couldn't load weekly trends</p>
            <p className="mt-1 text-sm text-red-500">Please check your connection and try again.</p>
            <button onClick={fetchData} className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">Retry</button>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              <SummaryCard icon={FolderPlus} title="Position Folders opened" accent="indigo" trend={data?.pf_trend} unit="total" weeks={weeks} />
              <SummaryCard icon={Send} title="Offers sent" accent="emerald" trend={data?.offer_trend} unit="total" weeks={weeks} />
            </div>

            {/* Combined chart */}
            <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-slate-800">PFs opened vs Offers sent</h2>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} labelFormatter={(l, p) => {
                    const d = p && p[0] && p[0].payload; return d ? `Week of ${d.week_start}${d.partial ? " (partial)" : ""}` : l;
                  }} />
                  <Legend />
                  <Bar dataKey="pfs_opened" name="PFs opened" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={38} />
                  <Bar dataKey="offers_sent" name="Offers sent" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={38} />
                  <Line type="monotone" dataKey="pfs_opened" name="PFs trend" stroke="#4338ca" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="offers_sent" name="Offers trend" stroke="#047857" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Two dedicated bar charts */}
            <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800"><FolderPlus className="h-5 w-5 text-indigo-500" /> PFs opened per week</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="pfs_opened" name="PFs opened" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={44} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800"><Send className="h-5 w-5 text-emerald-500" /> Offers sent per week</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="offers_sent" name="Offers sent" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={44} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Data table */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-4">
                <h2 className="text-lg font-semibold text-slate-800">Week-by-week breakdown</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="weekly-trends-table">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-6 py-3">Week</th>
                      <th className="px-6 py-3 text-right">PFs opened</th>
                      <th className="px-6 py-3 text-right">Δ vs prev</th>
                      <th className="px-6 py-3 text-right">Offers sent</th>
                      <th className="px-6 py-3 text-right">Δ vs prev</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((w, i) => {
                      const prev = rows[i + 1];
                      const pfD = prev ? w.pfs_opened - prev.pfs_opened : null;
                      const ofD = prev ? w.offers_sent - prev.offers_sent : null;
                      const cell = (d) => d == null ? <span className="text-slate-300">—</span> :
                        <span className={d > 0 ? "text-emerald-600" : d < 0 ? "text-red-600" : "text-slate-400"}>{d > 0 ? "+" : ""}{d}</span>;
                      return (
                        <tr key={w.week_start} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`trend-row-${w.week_start}`}>
                          <td className="px-6 py-3">
                            <span className="font-medium text-slate-800">{w.week_start}</span>
                            <span className="text-slate-400"> → {w.week_end}</span>
                            {w.partial && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">partial</span>}
                          </td>
                          <td className="px-6 py-3 text-right font-semibold text-indigo-700">{w.pfs_opened}</td>
                          <td className="px-6 py-3 text-right">{cell(pfD)}</td>
                          <td className="px-6 py-3 text-right font-semibold text-emerald-700">{w.offers_sent}</td>
                          <td className="px-6 py-3 text-right">{cell(ofD)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="px-6 py-3 text-xs text-slate-400">
                "Offers sent" counts offer/quote emails in the archive plus system-generated offers. Current week is partial. Weeks run Monday–Sunday.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Email report modal */}
      {emailOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="email-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                <Mail className="h-5 w-5 text-indigo-600" /> Email this analysis
              </h3>
              <button onClick={() => setEmailOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="email-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Recipients</label>
                <input
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="advisor@example.com, second@example.com"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  data-testid="email-recipients"
                />
                <p className="mt-1 text-xs text-slate-400">Separate multiple emails with commas.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Message (optional)</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                  placeholder="Add a short note for your advisors…"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  data-testid="email-message"
                />
              </div>
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Includes the summary, the week-by-week table for the selected <b>{weeks}-week</b> range, and a CSV attachment.
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button onClick={() => setEmailOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={sending}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                data-testid="email-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
